import { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatRupiah } from '../../utils/currency';
import { formatNumber } from '../../utils/number';

const COLORS = ['#0b2464', '#f5a623', '#10b981', '#6366f1'];
const PERIODS = [
  { key: 'daily', label: 'Harian' },
  { key: 'monthly', label: 'Bulanan' },
  { key: 'yearly', label: 'Tahunan' },
];

const SalesByPeriodChart = ({ data, period }) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item) => ({
      ...item,
      label: period === 'yearly'
        ? item.period.slice(0, 4)
        : period === 'daily'
          ? item.period.slice(5)
          : item.period.slice(5, 7),
    }));
  }, [data, period]);

  if (!chartData.length) {
    return <div className="analytics-empty">Belum ada data penjualan.</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
        <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
        <Tooltip
          formatter={(value) => formatRupiah(value)}
          contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        />
        <Legend />
        <Bar dataKey="total_amount" name="Total" fill={COLORS[0]} radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const SalesByTypeChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="analytics-empty">Belum ada data per tipe properti.</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 10, left: 60, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
        <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#94a3b8" width={80} />
        <Tooltip
          formatter={(value) => formatRupiah(value)}
          contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        />
        <Bar dataKey="total_amount" name="Total" fill={COLORS[1]} radius={[0, 6, 6, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const SalesByLocationChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="analytics-empty">Belum ada data per lokasi.</div>;
  }

  const topLocations = data.slice(0, 10);

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={topLocations} layout="vertical" margin={{ top: 10, right: 10, left: 80, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
        <YAxis dataKey="location" type="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={90} />
        <Tooltip
          formatter={(value) => formatRupiah(value)}
          contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        />
        <Bar dataKey="total_amount" name="Total" fill={COLORS[2]} radius={[0, 6, 6, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const SalesAnalytics = ({ data, onPeriodChange, onGroupChange, onLevelChange, period, group, level }) => {
  const tabs = [
    { key: 'period', label: 'Per Periode' },
    { key: 'property_type', label: 'Per Tipe' },
    { key: 'location', label: 'Per Lokasi' },
  ];

  const renderChart = () => {
    switch (group) {
      case 'property_type':
        return <SalesByTypeChart data={data} />;
      case 'location':
        return <SalesByLocationChart data={data} />;
      default:
        return <SalesByPeriodChart data={data} period={period} />;
    }
  };

  return (
    <div className="section-card">
      <div className="section-card__header">
        <h3 className="section-card__title">Analisis Penjualan</h3>
      </div>

      <div className="section-card__tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`section-card__tab ${group === tab.key ? 'section-card__tab--active' : ''}`}
            onClick={() => onGroupChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="section-card__body">
        {group === 'period' && (
          <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
            {PERIODS.map((p) => (
              <button
                key={p.key}
                className={`section-card__tab ${period === p.key ? 'section-card__tab--active' : ''}`}
                onClick={() => onPeriodChange(p.key)}
                style={{ borderBottom: 'none', padding: '4px 12px', fontSize: 12 }}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        {group === 'location' && (
          <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
            {['province', 'city', 'district'].map((lvl) => (
              <button
                key={lvl}
                className={`section-card__tab ${level === lvl ? 'section-card__tab--active' : ''}`}
                onClick={() => onLevelChange(lvl)}
                style={{ borderBottom: 'none', padding: '4px 12px', fontSize: 12 }}
              >
                {lvl === 'province' ? 'Provinsi' : lvl === 'city' ? 'Kota' : 'Kecamatan'}
              </button>
            ))}
          </div>
        )}

        {renderChart()}
      </div>
    </div>
  );
};

export default SalesAnalytics;
